import React, { useMemo } from "react";
import TapLocationSurface from "../core/TapLocationSurface";
import useWindowWidth from "../../../hooks/useWindowWidth";
import { EDGE } from "../core/edgeTokens";
import Candle from "./Candle";
import { besideX, CANDLE_X, candleGeometry, priceDomain, priceTicks, TYPE_DESCRIPTION } from "./candleModel";
import { DIAMOND_STATES } from "../core/FeedbackMarker";

// A single-candle chart wired to the reusable tap surface: light canvas, the
// candle in the centre, a price axis on the right, no grid.
// Mobile (below the md breakpoint) draws a taller canvas so the lesson fills
// more of the phone screen and the candle and its tap targets are larger. Only
// the vertical scale changes: zones are in price units, so grading is identical.
const W = 360;
const H_DESKTOP = 320;
const H_MOBILE = 500;
const plotFor = (h) => ({ x0: 0, x1: 312, y0: 18, y1: h - 18 });

const X_DOMAIN = [-3.9, 3.9]; // 40px per candle slot

// heights: optional { mobile, desktop } override, e.g. the smaller candle in a
// correction card.
export default function CandleChart({ ohlc, highlight, heights, markers, target, ...surfaceProps }) {
  const mobile = useWindowWidth() < 768;
  const H = mobile ? (heights && heights.mobile) || H_MOBILE : (heights && heights.desktop) || H_DESKTOP;
  const plot = useMemo(() => plotFor(H), [H]);
  const yDomain = useMemo(() => priceDomain(ohlc), [ohlc]);
  const ticks = useMemo(() => priceTicks(yDomain), [yDomain]);
  const { type } = candleGeometry(ohlc);
  const colourWord = { green: "Green", red: "Red", doji: "Doji", zeroRange: "Flat" }[type];

  // One placement rule for every diamond: keep its price (y) and move it to
  // the candle's outer edge on its label's side, so its point touches the spot
  // it marks. Checks and Call It dots stay exactly where they were tapped.
  const placedMarkers = useMemo(
    () =>
      (markers || []).map((m) =>
        DIAMOND_STATES.includes(m.state) ? { ...m, x: besideX(ohlc, m.y, m.side || "left") } : m
      ),
    [markers, ohlc]
  );
  const placedTarget = useMemo(() => {
    if (!target) return target;
    const side = target.side || "left";
    return { ...target, side, point: { x: besideX(ohlc, target.point.y, side), y: target.point.y } };
  }, [target, ohlc]);

  return (
    <TapLocationSurface
      width={W}
      height={H}
      plot={plot}
      xDomain={X_DOMAIN}
      yDomain={yDomain}
      description={`${colourWord} candle, ${TYPE_DESCRIPTION[type]}`}
      markers={placedMarkers}
      target={placedTarget}
      {...surfaceProps}
    >
      {(scale) => (
        <>
          {ticks.map((t) => (
            <text key={t} x={W - 4} y={scale.y(t) + 3.5} textAnchor="end" fontSize={10} fill={EDGE.neutral}>
              {t.toFixed(2)}
            </text>
          ))}
          <Candle ohlc={ohlc} scale={scale} bodyHalf={CANDLE_X.bodyHalf} highlight={highlight} />
        </>
      )}
    </TapLocationSurface>
  );
}
